import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  IconBadgeStatus,
  Spinner,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  CheckIcon,
  CloseIcon,
  PencilIcon,
  InactiveIcon,
  DownloadIcon,
  NotStartedIcon,
  ApplicationIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import useCheckFormExistsQuery from '../../../../hooks/data-fetching/useCheckFormExistsQuery';
import useIntl from '../../../../hooks/useIntl';
import HouseholdMemberCardContent from '../HouseholdMemberCardContent';
import HouseholdMemberListItem from '../HouseholdMemberListItem';
import { useApplicantDeniedReason } from './hooks';
import { shouldBypassMoveInRequirements } from '../../utils';

function getIconBadgeStatus(
  isCanceled,
  allItemsComplete,
  bypassMoveInRequirement,
) {
  if (isCanceled) {
    return 'canceled';
  }
  return allItemsComplete || bypassMoveInRequirement
    ? 'complete'
    : 'incomplete';
}

function getApprovalStatusIcon(isCanceled, isPending) {
  if (isCanceled) {
    return <CloseIcon fontSize="medium" />;
  }
  if (isPending) {
    return <NotStartedIcon fontSize="medium" color="error" />;
  }
  return <CheckIcon fontSize="medium" />;
}

function DetailIcon({ isSpecificItemComplete, bypassMoveInRequirement }) {
  const intl = useIntl();

  if (bypassMoveInRequirement) {
    return (
      <Tooltip
        title={intl.formatMessage({
          id: 'App.HouseholdApplicationsDrawer.ExternalSourceTooltip',
          defaultMessage: 'Not Required',
        })}
        placement="top"
        variant="light"
      >
        <InactiveIcon fontSize="medium" />
      </Tooltip>
    );
  }

  if (isSpecificItemComplete) {
    return <CheckIcon fontSize="medium" color="success" />;
  }
  return <NotStartedIcon fontSize="medium" color="error" />;
}

const DETAIL_ITEMS = ['Application', 'Application Checklist'];

export default function HouseholdMemberCardDetails({
  applicationComplete,
  checklistApproved,
  downloadAffordaPortalApplicationPDF,
  handleEditApplicationForm,
  isAdult,
  isResident,
  leaseSignerType,
  name,
  portalUserId,
  affordaPortaV1,
  handleChecklistClick,
  handleApprovalStatusClick,
  isLoading,
  isCanceled = false,
  isPending = false,
  deniedReasons,
  applicant,
  selectedProperty,
}) {
  const bypassMoveInRequirement =
    shouldBypassMoveInRequirements(selectedProperty);
  const { deniedReasonId } = useApplicantDeniedReason({
    applicantId: applicant.id,
    isCanceled,
  });
  const { data: formDataExists } = useCheckFormExistsQuery({ portalUserId });
  const specificItemComplete = useCallback(
    (item) =>
      isResident ||
      (item === 'Application Checklist' && checklistApproved) ||
      (item === 'Application' && applicationComplete),
    [isResident, checklistApproved, applicationComplete],
  );
  const checklistApprovedAndApplicationComplete =
    checklistApproved && applicationComplete;
  const allItemsComplete =
    isResident || (checklistApprovedAndApplicationComplete && !isPending);
  const hasPortalAccount = Boolean(portalUserId);

  return (
    <HouseholdMemberCardContent
      avatar={
        <IconBadgeStatus
          variant={getIconBadgeStatus(
            isCanceled,
            allItemsComplete,
            bypassMoveInRequirement,
          )}
        />
      }
      title={name}
      subheader={leaseSignerType}
    >
      {DETAIL_ITEMS.map((item) => {
        const onEditClickHandler =
          item === 'Application'
            ? handleEditApplicationForm
            : handleChecklistClick;

        return (
          <HouseholdMemberListItem
            key={item}
            onClick={onEditClickHandler}
            disabled={
              isLoading ||
              (item === 'Application' && (isResident || isCanceled))
            }
            icon={
              <DetailIcon
                isSpecificItemComplete={specificItemComplete(item)}
                bypassMoveInRequirement={bypassMoveInRequirement}
              />
            }
            primaryText={item}
            actionIcon={
              item === 'Application' && isCanceled ? null : (
                <PencilIcon color={'primary'} />
              )
            }
          />
        );
      })}
      {isAdult && hasPortalAccount && affordaPortaV1 && (
        <HouseholdMemberListItem
          key={'downloadAffordaPortalApplicationPDF'}
          onClick={() =>
            downloadAffordaPortalApplicationPDF.mutate({
              portalUserId,
              name,
            })
          }
          disabled={
            isLoading ||
            !formDataExists ||
            isResident ||
            downloadAffordaPortalApplicationPDF.isLoading
          }
          icon={<ApplicationIcon />}
          primaryText={'Application Details'}
          actionIcon={
            downloadAffordaPortalApplicationPDF.isLoading ? (
              <Spinner small />
            ) : (
              <DownloadIcon color={'primary'} />
            )
          }
        />
      )}
      {(isCanceled || isPending) && (
        <HouseholdMemberListItem
          onClick={isCanceled ? null : handleApprovalStatusClick}
          disabled={isLoading}
          icon={getApprovalStatusIcon(isCanceled, isPending)}
          primaryText={'Approval Status'}
          actionIcon={isCanceled ? null : <PencilIcon color={'primary'} />}
        />
      )}
      {isCanceled && (
        <Box sx={{ ml: 8 }}>
          <Typography variant="bodySmall" color="text.disabled">
            {`Reason: ${
              deniedReasons.find((reason) => reason.id === deniedReasonId)
                ?.reason
            }`}
          </Typography>
        </Box>
      )}
    </HouseholdMemberCardContent>
  );
}
